import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const category = categories.find((c) => c.slug === slug);

  const categoryArticles = category
    ? slug === 'hot'
      ? articles.filter((a) => a.isHot)
      : articles.filter((a) => a.categorySlug === slug)
    : [];

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-foreground-300">
              <i className="ri-emotion-sad-line text-4xl"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">
              分类未找到
            </h1>
            <p className="text-foreground-500 text-sm mb-4">该分类不存在或已被移除</p>
            <Link
              to="/"
              className="px-6 py-2.5 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer text-sm inline-flex items-center gap-2"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </div>
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const pageTitle = `${category.name} - 黑料入口 | 最新${category.name}吃瓜内容`;
  const pageDescription = `${category.description}。黑料入口为你提供最新、最全的${category.name}资讯，每日更新，不错过任何精彩内容。`;
  const pageKeywords = `${category.name},黑料入口,吃瓜黑料入口,黑料入口最新地址,黑料吃瓜在线,${category.name}黑料`;

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://www.heiliaorukou.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: category.name,
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        keywords={pageKeywords}
        canonicalUrl={`https://www.heiliaorukou.com/category/${slug}`}
        jsonLd={breadcrumbJsonLd}
      />

      <div className="min-h-screen">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: category.name }]} />

            <div className="mb-8">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center bg-accent-100 text-accent-600 rounded-lg">
                  <i className={`${category.icon} text-xl`}></i>
                </div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  {category.name}
                </h1>
              </div>
              <p className="text-foreground-500 text-sm max-w-2xl">
                {category.description}
              </p>
              <p className="text-xs text-foreground-400 mt-1">
                共 {categoryArticles.length} 篇文章
              </p>
            </div>

            {/* Category Tabs */}
            <div className="mb-8 overflow-x-auto pb-1">
              <div className="flex items-center gap-2 min-w-max p-1 bg-background-100 rounded-full">
                {categories.map((cat) => {
                  const isActive = cat.slug === slug;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => navigate(`/category/${cat.slug}`)}
                      className={`px-4 py-2 text-sm font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${
                        isActive
                          ? 'bg-primary-500 text-background-50'
                          : 'text-foreground-600 hover:bg-background-200/60 hover:text-foreground-900'
                      }`}
                    >
                      <span className="flex items-center gap-1.5">
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className={`${cat.icon} text-xs`}></i>
                        </div>
                        {cat.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 pb-14">
                {categoryArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-foreground-300">
                  <i className="ri-inbox-line text-4xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}